import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import SEO from "@/components/SEO";
import { getService, services } from "@/data/services";
import { cities } from "@/data/cities";
import { RevealBlock } from "@/components/Reveal";
import { TID } from "@/lib/testIds";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  if (!service) return <Navigate to="/services" replace />;

  const related = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`${service.name} in Nova Scotia`}
        description={service.intro.slice(0, 180)}
        path={`/services/${service.slug}`}
        image={service.hero}
        keywords={service.keywords}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.name,
          serviceType: "Security services",
          areaServed: { "@type": "AdministrativeArea", name: "Nova Scotia" },
          provider: { "@type": "SecurityService", name: "Nova Scotia Security Services" },
          description: service.intro,
        }}
      />

      <section
        ref={ref}
        data-testid={`${TID.services.detail}-${service.slug}`}
        className="relative"
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 md:pt-20">
          <Link
            to="/services"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/60 hover:text-[#C17A43]"
          >
            ← All Services
          </Link>

          <div className="grid grid-cols-12 gap-4 mt-12 md:mt-16 items-end">
            <div className="col-span-12 lg:col-span-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6">
                Chapter {service.number}
              </p>
              <RevealBlock>
                <h1 className="font-serif text-[clamp(3rem,9vw,9rem)] leading-[0.88] tracking-tighter text-[#111111]">
                  {service.name}
                </h1>
              </RevealBlock>
              <p className="mt-8 font-serif italic text-2xl md:text-3xl text-[#184C59] max-w-[36ch] leading-tight">
                {service.tagline}
              </p>
            </div>
            <div className="col-span-12 lg:col-span-4 lg:pb-6 border border-[#D9D6D0] p-8 bg-[#F5F3EF]">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43] mb-4">
                — Deliverables
              </p>
              <ul className="space-y-2">
                {service.deliverables?.map((d) => (
                  <li key={d} className="font-sans text-sm text-[#111111]/80 flex gap-2">
                    <span className="text-[#C17A43]">✦</span> {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="relative mt-16 md:mt-20 overflow-hidden aspect-[21/9] max-w-[1600px] mx-auto md:px-12 px-6">
          <div className="relative w-full h-full overflow-hidden">
            <motion.img
              style={{ y: imgY }}
              src={service.hero}
              alt={`${service.name} in Nova Scotia`}
              className="absolute inset-0 w-full h-[130%] object-cover grayscale contrast-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/50 to-transparent" />
            <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5F3EF]">
              Fig. {service.number} · {service.name}
            </p>
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 md:py-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] sticky top-32">
              — Overview
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <RevealBlock>
              <p className="font-serif italic text-3xl md:text-5xl leading-tight text-[#111111] mb-12">
                {service.intro}
              </p>
            </RevealBlock>
            {service.body.map((p, i) => (
              <RevealBlock key={i} delay={i * 0.08}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                  {p}
                </p>
              </RevealBlock>
            ))}
            {/* Universal additions extending every service page's word count for SEO */}
            <RevealBlock>
              <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                Every {service.name.toLowerCase()} engagement is opened with a written brief, briefed to a licensed supervisor, and closed with a signed daily activity report. Officers are rostered from within Nova Scotia — never sub-contracted, never imported — and are cross-trained across de-escalation, first aid, incident documentation, and site-specific protocols before their first shift.
              </p>
            </RevealBlock>
            <RevealBlock delay={0.05}>
              <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                Licensing, insurance, and compliance obligations for {service.name.toLowerCase()} contracts in Nova Scotia are held under the provincial Security and Investigative Services Act, WCB coverage, and $5M commercial general liability. Every officer holds a current provincial licence, Standard First Aid + CPR-C, and site-specific WHMIS or trauma-informed training where applicable.
              </p>
            </RevealBlock>
            <RevealBlock delay={0.1}>
              <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                Because we deliberately do not publish a phone number or an email, every {service.name.toLowerCase()} engagement begins with a written brief — mailed to our Halifax office or delivered by letter of intent through your legal counsel. This discipline keeps the record clean and the accountability precise.
              </p>
            </RevealBlock>

            {/* SEO Keywords - visually presented */}
            {service.keywords?.length > 3 && (
              <div className="mt-16 border-t border-[#D9D6D0] pt-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/50 mb-4">
                  — Commonly searched
                </p>
                <div className="flex flex-wrap gap-2">
                  {service.keywords.map((k) => (
                    <span
                      key={k}
                      className="font-mono text-[10px] uppercase tracking-[0.18em] border border-[#D9D6D0] px-3 py-1.5 text-[#111111]/70"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related services */}
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-8">
            — Continue Reading
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D9D6D0] border border-[#D9D6D0]">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}`}
                className="group bg-[#F5F3EF] p-8 hover:bg-[#111111] hover:text-[#F5F3EF] transition-colors duration-500 min-h-[220px] flex flex-col justify-between"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-60">
                  {r.number}
                </p>
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl leading-tight group-hover:italic transition-[font-style] duration-300">
                    {r.name}
                  </h3>
                  <p className="mt-3 font-sans text-sm opacity-70">{r.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Local coverage — every city gets a combo page for this service */}
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-32">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6">
            — {service.name} · Delivered across every Nova Scotia community
          </p>
          <p className="font-sans text-sm md:text-base text-[#111111]/70 max-w-[64ch] mb-10 leading-relaxed">
            Every community below carries a dedicated landing page for{" "}
            {service.name.toLowerCase()} — with locally-tuned deliverables, keyword coverage, and route intelligence.
          </p>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1.5 border-t border-[#111111] pt-6">
            {cities.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/services/${service.slug}/${c.slug}`}
                  className="group flex items-baseline justify-between border-b border-[#D9D6D0] py-2 hover:border-[#111111]"
                >
                  <span className="font-serif text-lg text-[#111111]/80 group-hover:italic group-hover:text-[#C17A43] transition-colors">
                    {service.shortName || service.name} · {c.name}
                  </span>
                  <span className="opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
